import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import PlusIcon from '../../assets/svg/PlusIcon';
import { UploadsTable } from '../../components/AidentifiedData';
import { SearchFilter } from '../../components/CallLog/SearchFilter';
import {
  AidentifiedReportModal,
  AidentifiedUploadModal,
  DeleteUploadModal,
} from '../../components/Modals';
import { Button } from '../../components/Shared';
import { Layout } from '../../layouts';
import {
  getActiveClients,
  getAidentifiedUploads,
} from '../../services/aidentifiedDataApi';

const INITIAL_FILTERS = { search: '', tenantId: '' };

export const AidentifiedData = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [clients, setClients] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [isUploadsLoading, setIsUploadsLoading] = useState(true);
  const [isUploadsError, setIsUploadsError] = useState(false);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  // Opened with a client from the AI Prompts page.
  const [uploadModal, setUploadModal] = useState(
    location.state?.uploadForTenantId
      ? { tenantId: location.state.uploadForTenantId }
      : null,
  );
  const [reportUpload, setReportUpload] = useState(null);
  const [deleteUpload, setDeleteUpload] = useState(null);

  const fetchUploads = useCallback(async () => {
    setIsUploadsLoading(true);
    setIsUploadsError(false);

    try {
      setUploads(await getAidentifiedUploads());
    } catch {
      setIsUploadsError(true);
    } finally {
      setIsUploadsLoading(false);
    }
  }, []);

  const fetchClients = useCallback(async () => {
    try {
      setClients(await getActiveClients());
    } catch {
      toast.error('Failed to load clients!', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  }, []);

  useEffect(() => {
    fetchUploads();
    fetchClients();
  }, [fetchUploads, fetchClients]);

  // A reload of the page must not open the upload window again.
  useEffect(() => {
    if (location.state?.uploadForTenantId) {
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location, navigate]);

  const filteredUploads = useMemo(() => {
    const search = filters.search.trim().toLowerCase();

    return uploads.filter(
      ({ tenantId, tenantName, fileName }) =>
        (!filters.tenantId || tenantId === Number(filters.tenantId)) &&
        (fileName.toLowerCase().includes(search) ||
          tenantName.toLowerCase().includes(search)),
    );
  }, [uploads, filters]);

  const isFiltersChanged =
    filters.search !== INITIAL_FILTERS.search ||
    filters.tenantId !== INITIAL_FILTERS.tenantId;

  return (
    <Layout>
      <div className="flex flex-col gap-4 h-full overflow-hidden">
        <div className="flex items-center justify-between gap-3 shrink-0">
          <h1 className="text-base-1000 text-[22px]/[28px] font-semibold py-2.5">
            Aidentified Data
          </h1>
          <Button
            title="Upload file"
            iconStart={<PlusIcon className="w-3 h-3" />}
            onClick={() => setUploadModal({})}
          />
        </div>
        <div className="flex gap-3 overflow-auto shrink-0">
          <SearchFilter
            key={filters.search}
            search={filters.search}
            onChangeSearch={setFilters}
          />
          <label className="flex items-center gap-1 rounded-full py-[11px] px-5 text-brand-900 border border-brand-900 font-medium text-[16px]/[24px] text-nowrap">
            Client:
            <select
              value={filters.tenantId}
              onChange={e => {
                const { value } = e.target;
                setFilters(prev => ({ ...prev, tenantId: value }));
              }}
              className="bg-transparent outline-none cursor-pointer max-w-[240px] text-align-last-left"
            >
              <option value="">All clients</option>
              {clients.map(({ TenantID, TenantName }) => (
                <option key={TenantID} value={TenantID}>
                  {TenantName}
                </option>
              ))}
            </select>
          </label>
          {isFiltersChanged && (
            <Button
              title="Clear All"
              variant="text"
              onClick={() => setFilters(INITIAL_FILTERS)}
              className="!bg-transparent hover:!bg-transparent focus:!bg-transparent text-brand-900 p-2"
            />
          )}
        </div>
        <UploadsTable
          uploads={filteredUploads}
          isFiltersChanged={isFiltersChanged}
          isUploadsLoading={isUploadsLoading}
          isUploadsError={isUploadsError}
          fetchUploads={fetchUploads}
          onOpenReport={setReportUpload}
          onOpenDelete={setDeleteUpload}
        />
        {uploadModal && (
          <AidentifiedUploadModal
            clients={clients}
            initialTenantId={uploadModal.tenantId}
            onClose={() => setUploadModal(null)}
            onUploaded={fetchUploads}
          />
        )}
        {reportUpload && (
          <AidentifiedReportModal
            upload={reportUpload}
            onClose={() => setReportUpload(null)}
          />
        )}
        {deleteUpload && (
          <DeleteUploadModal
            upload={deleteUpload}
            onClose={() => setDeleteUpload(null)}
            onDeleted={importId => {
              setUploads(prev =>
                prev.filter(upload => upload.importId !== importId),
              );
              setDeleteUpload(null);
            }}
          />
        )}
      </div>
    </Layout>
  );
};
